import { Translations } from '../types';

export const translations: Record<'fr' | 'en' | 'es', Translations> = {
  fr: {
    nav: {
      home: 'Accueil',
      mission: 'Expertise',
      clients: 'Clients',
      offers: 'Accompagnement',
      contact: 'Contact',
      about: 'À Propos',
    },
    hero: {
      title: 'Votre projet est entre de bonnes mains !',
      description:
        'Que vous ayez déjà un prototype fonctionnel ou juste une idée, nous vous accompagnons dans le développement de votre produit, de la définition technique à l\'industrialisation, en passant par la réalisation de POCs, de MVPs et de prototypes.',
      ctaContact: 'Parlons de votre projet',
      capabilityStrip: {
        ariaLabel: 'Compétences Sparkr',
        items: [
          {
            id: 'software',
            title: 'Logiciel',
            line: 'Applications Web2/3 & mobiles, smart contracts, cloud',
          },
          {
            id: 'embedded',
            title: 'Embarqué',
            line: 'Firmware, BSPs, pilotes',
          },
          {
            id: 'hardware',
            title: 'Électronique',
            line: 'PCB, RF, schémas, prototypage',
          },
          {
            id: 'reverseEngineering',
            title: 'Rétro-ingénierie',
            line: 'Logiciel, matériel, RF, analyse',
          },
          {
            id: 'aiAssisted',
            title: 'Assisté par IA',
            line: 'Usage rigoureux, pilotée par des experts, déployable en production',
          },
          {
            id: 'industrialization',
            title: 'Industrialisation',
            line: 'Préséries, fabrication, qualité, volumes',
          },
        ],
      },
    },
    mission: {
      title: 'Notre expertise',
      description: 'Sparkr propose un accompagnement complet et sur-mesure, avec une vaste expertise dans des domaines tels que l\'IA, l\'IOT, la RF, la robotique, les smartphones et tablettes Android ou encore la blockchain.',
      intro: 'Notre savoir-faire nous permet de couvrir l\'ensemble des compétences techniques mises en jeu lors de la création d\'un produit ou d\'un service :',
      expertise: [
        'conception mécanique',
        'design de PCB',
        'développement embarqué, middleware, applicatif et cloud',
        'développement d\'applications Web3 et de smart-contracts',
        'développement de POCs et MVPs',
        'rétro-ingénierie logicielle, matérielle et RF',
        'suivi de projet',
        'préparation, lancement et suivi de production',
      ],
      aiTitle: 'IA et développement : aller plus vite, avec l\'exigence d\'un pilotage expert',
      aiColumnHelpsLabel: 'L\'IA fait gagner du temps',
      aiColumnLimitsLabel: 'L\'expertise garantit le résultat',
      aiHelps: [
        'L\'IA accélère l\'exploration, les itérations, la production de code, les tests et la documentation, lorsqu\'elle est utilisée dans un cadre méthodique et rigoureux.',
        'Nous l\'utilisons pour livrer plus vite des POC et MVP, en particulier applicatifs/cloud, avec un pilotage expert qui garantit une architecture saine, un niveau de sécurité irréprochable et une base maintenable.',
      ],
      aiLimits: [
        'Validation produit, essais, EMC/RF, qualification et certifications : les décisions reposent sur des preuves mesurées, validées au labo et sur le terrain.',
        'Industrialisation, supply chain et mise en production : des enjeux opérationnels qui exigent expérience, méthode et maîtrise, au-delà de la simple démonstration logicielle.',
      ],
    },
    services: {
      title: 'Nos services',
      description: 'Nous intervenons à tous les niveaux de la chaîne, du développement à l\'industrialisation :',
      trackSoftware: 'Logiciel & cloud',
      trackHardware: 'Matériel & embarqué',
      trackBoth: 'Logiciel & matériel',
      items: [
        {
          image: '/images/Design.jpg',
          title: 'Définition technique de produits (architecture logicielle, électronique et mécanique)',
          alt: 'Définition technique de produits',
          track: 'both',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/POC.jpg',
          title: 'Développement et réalisation de POC (pour tester un algorithme, un composant ou une fonctionnalité spécifique)',
          alt: 'Développement de POC',
          track: 'both',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/MVP.jpg',
          title: 'Développement et réalisation de MVP (produit minimum viable pour valider le marché et les usages)',
          alt: 'Développement de MVP',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/Features.jpg',
          title: 'Développement de fonctionnalités clés',
          alt: 'Développement de fonctionnalités clés',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/ReverseEngineeringSoftware.jpg',
          title: 'Rétro-ingénierie logicielle (désassemblage, extraction algorithmique)',
          alt: 'Rétro-ingénierie logicielle',
          track: 'software',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineering.jpg',
          title: 'Rétro-ingénierie matérielle (cartes, schémas, extraction de firmware, analyse de l\'existant)',
          alt: 'Rétro-ingénierie matérielle',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineeringRF.jpg',
          title: 'Rétro-ingénierie RF (protocoles radio, modulation, codage)',
          alt: 'Rétro-ingénierie RF',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/Prototypes.jpg',
          title: 'Développement et réalisation de prototypes',
          alt: 'Développement de prototypes',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Preseries.jpg',
          title: 'Réalisation de préséries',
          alt: 'Réalisation de préséries',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Certifications.jpg',
          title: 'Certifications',
          alt: 'Certifications',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Massprod.jpg',
          title: 'Industrialisation à grande échelle',
          alt: 'Industrialisation à grande échelle',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
      ],
    },
    clients: {
      title: 'Nos clients',
      description:
        'Nous accompagnons des acteurs de l\'industrie et de la tech sur des projets embarqués, connectés et innovants. Voici quelques organisations avec lesquelles nous avons travaillé.',
      items: [
        { name: 'Archos' },
        { name: 'Arkéa' },
        { name: 'Compilons' },
        { name: 'Delta Drone' },
        { name: 'Lunii' },
        { name: 'Securely' },
      ],
    },
    offerCards: {
      title: 'Notre accompagnement',
      description:
        'Découvrez nos trois offres d\'accompagnement, conçues pour répondre parfaitement à vos besoins',
      learnMore: 'En savoir plus',
    },
    offerPages: [
      {
        id: 'a-to-z',
        slug: 'idea-to-production',
        cardImage: '/images/Design.jpg',
        cardImageAlt: 'Définition technique et conception produit',
        title: 'De l\'idée à la production',
        subtitle: 'Conception, prototypage, certification et industrialisation',
        cardClientNeed:
          'Lorsque vous devez décider, valider et industrialiser, de la faisabilité jusqu\'à la production',
        cardTeaser:
          'Architecture produit, prototypes, préséries, certifications et suivi fournisseurs : un interlocuteur unique, avec des livrables concrets à chaque étape, qu\'il s\'agisse de besoins ciblés ou d\'un accompagnement sur l\'ensemble du cycle.',
        pageIntro:
          'De la première étude de faisabilité à la mise en production de masse, Sparkr couvre l\'ensemble du cycle produit avec une exigence de qualité constante à chaque étape, qu\'il s\'agisse d\'un besoin ciblé ou d\'un accompagnement bout en bout.',
        phases: [
          {
            title: 'Étude de faisabilité & architecture technique',
            subtitle: 'Lorsque vous devez décider quoi construire et comment',
            description:
              'Analyse des enjeux produit, validation de la faisabilité, choix d\'architecture et rédaction de spécifications techniques actionnables pour lancer le développement avec un cadre solide.',
          },
          {
            title: 'Prototypage & validation',
            subtitle: 'Lorsque vous devez stabiliser, tester, affiner',
            description:
              'Itérations matérielles et logicielles, bancs de test, essais utilisateurs et préparation des exigences pour une montée en maturité sereine.',
          },
          {
            title: 'Industrialisation',
            subtitle: 'Lorsque vous devez passer à l\'échelle, certifier, produire',
            description:
              'Préséries, industrialisation, qualification et certifications, avec un accompagnement complet jusqu\'à la mise en production, incluant le suivi de production et des fournisseurs.',
          },
        ],
        ctaLabel: 'Parlons de votre projet',
        seo: {
          title: 'De l\'idée à la production | Sparkr',
          description:
            'Sparkr vous accompagne de la définition technique à l\'industrialisation : faisabilité, prototypage, certifications et production en série.',
        },
      },
      {
        id: 'poc-mvp',
        slug: 'poc-mvp',
        cardImage: '/images/Features.jpg',
        cardImageAlt: 'Développement de POC et MVP',
        title: 'POC & MVP ultra-rapides',
        subtitle: 'Développement logiciel accéléré par IA, sous pilotage expert',
        cardClientNeed:
          'Lorsque vous devez construire vite, réduire le risque et prouver la valeur',
        cardTeaser:
          'POC et MVP applicatifs ou cloud livrés à un rythme soutenu : IA utilisée avec rigueur, pilotage expert, code maintenable et sécurité prise en compte dès le départ.',
        pageIntro:
          'Nous concevons et livrons des POCs et MVPs, en particulier logiciels, à un rythme accéléré, grâce à une utilisation maîtrisée de l\'IA sous pilotage expert. Le résultat : du code sûr et production-ready dès le départ.',
        phases: [
          {
            title: 'Réalisation de POC & MVP accélérés par IA',
            subtitle: 'Lorsque vous devez construire vite, réduire le risque, prouver la valeur',
            description:
              'Conception et réalisation ultra rapides, rendues possibles par une utilisation rigoureuse de l\'IA, pilotée par notre expertise technique. Cette approche nous permet d\'atteindre un niveau d\'exigence production-ready, alliant qualité, maintenabilité, sécurité by design et respect des bonnes pratiques.',
          },
        ],
        ctaLabel: 'Parlons de votre projet',
        seo: {
          title: 'POC & MVP ultra-rapides | Sparkr',
          description:
            'Sparkr conçoit et livre des POC et MVP logiciels à vitesse accélérée grâce à l\'IA, sous pilotage expert. Code production-ready dès le départ.',
        },
      },
      {
        id: 'reverse-engineering',
        slug: 'reverse-engineering',
        cardImage: '/images/ReverseEngineering.jpg',
        cardImageAlt: 'Rétro-ingénierie logicielle, matérielle et RF',
        title: 'Rétro-ingénierie & reprise technique',
        subtitle: 'Analyse de systèmes logiciels, électroniques et RF',
        cardClientNeed:
          'Lorsque vous devez comprendre l\'existant, documenter ou ré-ingénier',
        cardTeaser:
          'Cartes, firmware, protocoles et RF : nous restituons une vision exploitable pour interopérabilité, migration ou maintenance, dans le respect de vos contraintes.',
        pageIntro:
          'Vous devez comprendre un système existant, documenter son fonctionnement ou préparer une migration ? Nous analysons cartes, firmware, protocoles et comportement radio pour vous restituer une vision technique exploitable.',
        phases: [
          {
            title: 'Rétro-ingénierie & reprise technique',
            subtitle: 'Lorsque vous devez comprendre l\'existant, documenter, ré-ingénier',
            description:
              'Analyse de systèmes logiciels, électroniques et RF déjà déployés : cartes, firmware, protocoles et comportement radio. Nous restituons une vision exploitable (schémas, interfaces, risques) pour interopérabilité, migration, maintenance ou ré-ingénierie, dans un cadre adapté à vos contraintes.',
          },
        ],
        ctaLabel: 'Parlons de votre projet',
        seo: {
          title: 'Rétro-ingénierie logicielle, matérielle et RF | Sparkr',
          description:
            'Sparkr analyse vos systèmes existants (logiciel, électronique, RF) pour interopérabilité, migration ou ré-ingénierie. Documentation technique exploitable.',
        },
      },
    ],
    contact: {
      title: 'Nous contacter',
      description: 'Vous souhaitez un devis, ou voulez simplement discuter de votre projet avec nous ? N\'hésitez pas à nous contacter à l\'aide du formulaire ci-dessous ou par mail sur',
      suffix: 'en décrivant brièvement votre projet afin que nous puissions convenir d\'un rendez-vous.',
      email: 'contact@sparkr.tech',
    },
    about: {
      title: 'À propos de Sparkr',
      description: 'Sparkr est une société dirigée par',
      suffix: ', ingénieur de formation, passionné par les nouvelles technologies et l\'innovation, expert du développement et de l\'industrialisation de produits tech.',
      founders: [
        {
          name: 'Jean-Christophe Rona',
          linkedin: 'https://www.linkedin.com/in/jean-christophe-rona/',
          image: '/images/jc.jpg',
          alt: 'Jean-Christophe Rona',
        },
      ],
    },
    footer: {
      company: 'Sparkr',
      description: 'Votre partenaire pour le développement et l\'industrialisation de produits technologiques.',
      quickLinks: 'Liens rapides',
      services: 'Notre accompagnement',
      contact: 'Contact',
      legal: 'Légal',
      privacy: 'Confidentialité',
      terms: 'Conditions',
      followUs: 'Suivez-nous',
      copyright: '© Sparkr 2026',
      allRightsReserved: 'Tous droits réservés',
    },
    seo: {
      homeTitle: 'Sparkr',
      homeDescription:
        'Sparkr vous accompagne dans le développement de votre produit, de la définition technique à l\'industrialisation, en passant par la réalisation de POCs, de MVPs, de prototypes, le développement Web3/smart contracts et la rétro-ingénierie (logiciel, matériel, RF).',
      privacyTitle: 'Politique de confidentialité | Sparkr',
      privacyDescription:
        'Politique de confidentialité du site Sparkr : collecte et utilisation des données, cookies et vos droits.',
      termsTitle: 'Conditions générales d\'utilisation | Sparkr',
      termsDescription:
        'Conditions générales d\'utilisation du site web Sparkr.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      mission: 'Expertise',
      clients: 'Clients',
      offers: 'Engagements',
      contact: 'Contact',
      about: 'About',
    },
    hero: {
      title: 'Your project is in good hands!',
      description:
        'Whether you already have a working prototype or just an idea, we support you in developing your product, from technical definition to industrialization, including POCs, MVPs and prototypes.',
      ctaContact: 'Get in touch',
      capabilityStrip: {
        ariaLabel: 'Sparkr capabilities',
        items: [
          {
            id: 'software',
            title: 'Software',
            line: 'Web2/3 & mobile apps, smart contracts, cloud',
          },
          {
            id: 'embedded',
            title: 'Embedded',
            line: 'Firmware, BSPs, drivers',
          },
          {
            id: 'hardware',
            title: 'Hardware',
            line: 'PCB design, RF, schematics, prototyping',
          },
          {
            id: 'reverseEngineering',
            title: 'Reverse engineering',
            line: 'Software, hardware, RF, analysis',
          },
          {
            id: 'aiAssisted',
            title: 'AI-assisted',
            line: 'Rigorous use, expert-led, production-ready',
          },
          {
            id: 'industrialization',
            title: 'Industrialization',
            line: 'Pre-series, production, quality, scale',
          },
        ],
      },
    },
    mission: {
      title: 'Our expertise',
      description: 'Sparkr offers complete and customized support, with a wide range of expertise in areas such as IA, IOT, RF, robotics, Android smartphones and tablets, or blockchain.',
      intro: 'Our knowledge allows us to cover all the technical skills involved in the creation of a product or a service:',
      expertise: [
        'mechanical design',
        'PCB design',
        'embedded, middleware, application and cloud development',
        'Web3 apps and smart-contracts development',
        'development of POCs and MVPs',
        'software, hardware and RF reverse engineering',
        'project monitoring',
        'preparation, launch and follow-up of production',
      ],
      aiTitle: 'AI and development: moving faster, with the discipline of expert oversight',
      aiColumnHelpsLabel: 'AI saves time',
      aiColumnLimitsLabel: 'Expertise guarantees the outcome',
      aiHelps: [
        'AI speeds up exploration, iteration, code production, testing, and documentation when used within a methodical and rigorous framework.',
        'We use it to deliver POCs and MVPs faster, especially on the application and cloud side, with expert oversight that ensures sound architecture, impeccable security, and a maintainable foundation.',
      ],
      aiLimits: [
        'Product validation, testing, EMC/RF, qualification, and certification: decisions are based on measurable evidence, validated in the lab and in the field.',
        'Industrialization, supply chain, and production rollout: operational challenges that require experience, method, and control, beyond a simple software demonstration.',
      ],
    },
    services: {
      title: 'Our services',
      description: 'We operate at all levels of the chain, from development to industrialization:',
      trackSoftware: 'Software & cloud',
      trackHardware: 'Hardware & embedded',
      trackBoth: 'Software & hardware',
      items: [
        {
          image: '/images/Design.jpg',
          title: 'Technical definition of products (software, electronic and mechanical architecture)',
          alt: 'Technical product definition',
          track: 'both',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/POC.jpg',
          title: 'Development and realization of POC (to test an algorithm, a component, or a specific functionality)',
          alt: 'POC development',
          track: 'both',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/MVP.jpg',
          title: 'Development and realization of MVP (minimum viable product to validate market and usage)',
          alt: 'MVP development',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/Features.jpg',
          title: 'Development of key features',
          alt: 'Key features development',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/ReverseEngineeringSoftware.jpg',
          title: 'Software reverse engineering (disassembly, algorithm extraction)',
          alt: 'Software reverse engineering',
          track: 'software',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineering.jpg',
          title: 'Hardware reverse engineering (boards, schematics, firmware extraction, analysis of existing systems)',
          alt: 'Hardware reverse engineering',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineeringRF.jpg',
          title: 'RF reverse engineering (radio protocols, modulation, encoding)',
          alt: 'RF reverse engineering',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/Prototypes.jpg',
          title: 'Development and production of prototypes',
          alt: 'Prototype development',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Preseries.jpg',
          title: 'Production of pre-series',
          alt: 'Pre-series production',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Certifications.jpg',
          title: 'Certifications',
          alt: 'Certifications',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Massprod.jpg',
          title: 'Mass production',
          alt: 'Mass production',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
      ],
    },
    clients: {
      title: 'Our clients',
      description:
        'We work with industrial and technology organizations on embedded, connected, and innovative products. Here are some of the organizations we have worked with.',
      items: [
        { name: 'Arkéa' },
        { name: 'Archos' },
        { name: 'Compilons' },
        { name: 'Delta Drone' },
        { name: 'Lunii' },
        { name: 'Securely' },
      ],
    },
    offerCards: {
      title: 'How we work with you',
      description:
        'Explore our three engagement models, designed to match your needs perfectly.',
      learnMore: 'Learn more',
    },
    offerPages: [
      {
        id: 'a-to-z',
        slug: 'idea-to-production',
        cardImage: '/images/Design.jpg',
        cardImageAlt: 'Technical product definition and design',
        title: 'From idea to production',
        subtitle: 'Design, prototyping, certification and industrialization',
        cardClientNeed:
          'When you need to decide, validate, and scale, from feasibility through volume production',
        cardTeaser:
          'Product architecture, prototypes, pre-series, certifications, and supplier follow-up: a single point of contact, with tangible deliverables at every step—whether you have targeted needs or support across the full lifecycle.',
        pageIntro:
          'From the initial feasibility study through to full-scale production, Sparkr covers the entire product lifecycle with consistent quality at every stage—whether you need a focused contribution or full journey support.',
        phases: [
          {
            title: 'Feasibility study & technical architecture',
            subtitle: 'When you need to decide what to build and how',
            description:
              'We analyze product challenges, validate feasibility, define the architecture, and produce actionable technical specifications to launch development with a solid framework.',
          },
          {
            title: 'Prototyping & validation',
            subtitle: 'When you need to stabilize, test, refine',
            description:
              'Hardware and software iterations, test benches, user trials, and preparation of requirements for a confident ramp-up.',
          },
          {
            title: 'Industrialization',
            subtitle: 'When you need to scale, certify, produce',
            description:
              'Pre-series runs, industrialization, qualification and certifications, with end-to-end support through production launch, including production and supplier follow-up.',
          },
        ],
        ctaLabel: 'Let\'s discuss your project',
        seo: {
          title: 'From idea to production | Sparkr',
          description:
            'Sparkr supports you from technical definition to industrialization: feasibility, prototyping, certifications and mass production.',
        },
      },
      {
        id: 'poc-mvp',
        slug: 'poc-mvp',
        cardImage: '/images/Features.jpg',
        cardImageAlt: 'POC and MVP development',
        title: 'Ultrafast POC & MVP',
        subtitle: 'AI-accelerated software delivery, with expert oversight',
        cardClientNeed: 'When you need to build fast, de-risk, and prove value',
        cardTeaser:
          'Software POCs and MVPs delivered at pace: disciplined AI use, expert oversight, maintainable code, and security considered from day one.',
        pageIntro:
          'We design and deliver POCs and MVPs, especially software ones, at an accelerated pace, powered by disciplined AI usage under expert oversight. The result: safe, production-ready code from day one.',
        phases: [
          {
            title: 'POC & MVP delivery accelerated with AI',
            subtitle: 'When you need to build fast, de-risk, prove value',
            description:
              'Ultra-fast design and delivery, made possible by disciplined AI usage guided by our technical expertise. This approach enables a production-ready level of quality from day one, combining code quality, maintainability, security by design, and industry best practices.',
          },
        ],
        ctaLabel: 'Let\'s discuss your project',
        seo: {
          title: 'Ultrafast POC & MVP | Sparkr',
          description:
            'Sparkr designs and delivers software POCs and MVPs at accelerated pace with AI, under expert oversight. Production-ready code from day one.',
        },
      },
      {
        id: 'reverse-engineering',
        slug: 'reverse-engineering',
        cardImage: '/images/ReverseEngineering.jpg',
        cardImageAlt: 'Software, hardware and RF reverse engineering',
        title: 'Reverse engineering & technical recovery',
        subtitle: 'Analysis of software, hardware and RF systems',
        cardClientNeed: 'When you need to understand the as-built, document, or re-engineer',
        cardTeaser:
          'Boards, firmware, protocols, and RF: we deliver actionable insight for interoperability, migration, or sustainment, scoped to your constraints.',
        pageIntro:
          'Need to understand a deployed system, document its behavior, or prepare a migration? We analyze boards, firmware, protocols, and radio behavior to deliver actionable technical documentation.',
        phases: [
          {
            title: 'Reverse engineering & technical recovery',
            subtitle: 'When you need to understand the as-built, document, re-engineer',
            description:
              'Structured analysis of deployed software, hardware, and RF systems: boards, firmware, protocols, and on-air behavior. We deliver actionable documentation (interfaces, risks, unknowns) to support interoperability, migration, sustainment, or redesign\u2014scoped to your constraints and compliance needs.',
          },
        ],
        ctaLabel: 'Let\'s discuss your project',
        seo: {
          title: 'Reverse engineering & technical recovery | Sparkr',
          description:
            'Sparkr analyzes deployed software, hardware and RF systems for interoperability, migration or redesign. Actionable technical documentation.',
        },
      },
    ],
    contact: {
      title: 'Contact us',
      description: 'Whether you need a quote or just want to discuss your project with us, do not hesitate to contact us using the form below or by email at',
      suffix: 'briefly describing your project so that we can schedule a meeting.',
      email: 'contact@sparkr.tech',
    },
    about: {
      title: 'About Sparkr',
      description: 'Sparkr is a company led by',
      suffix: ', engineer by training, fond of new technology and innovation, expert in the development and industrialization of tech products.',
      founders: [
        {
          name: 'Jean-Christophe Rona',
          linkedin: 'https://www.linkedin.com/in/jean-christophe-rona/',
          image: '/images/jc.jpg',
          alt: 'Jean-Christophe Rona',
        },
      ],
    },
    footer: {
      company: 'Sparkr',
      description: 'Your partner for the development and industrialization of technological products.',
      quickLinks: 'Quick Links',
      services: 'How we work with you',
      contact: 'Contact',
      legal: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms',
      followUs: 'Follow Us',
      copyright: '© Sparkr 2026',
      allRightsReserved: 'All rights reserved',
    },
    seo: {
      homeTitle: 'Sparkr',
      homeDescription:
        'Sparkr supports you in developing your product, from technical definition to industrialization, including POCs, MVPs, prototypes, Web3 apps/smart-contract development, and software/hardware/RF reverse engineering.',
      privacyTitle: 'Privacy Policy | Sparkr',
      privacyDescription:
        'Sparkr website privacy policy: how we collect and use data, cookies, and your rights.',
      termsTitle: 'Terms of Use | Sparkr',
      termsDescription: 'Terms of use for the Sparkr website.',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      mission: 'Experiencia',
      clients: 'Clientes',
      offers: 'Acompañamiento',
      contact: 'Contacto',
      about: 'Acerca de',
    },
    hero: {
      title: '¡Su proyecto está en buenas manos!',
      description:
        'Ya tenga un prototipo funcional o solo una idea, le acompañamos en el desarrollo de su producto, desde la definición técnica hasta la industrialización, pasando por POCs, MVPs y prototipos.',
      ctaContact: 'Hablemos de su proyecto',
      capabilityStrip: {
        ariaLabel: 'Capacidades de Sparkr',
        items: [
          {
            id: 'software',
            title: 'Software',
            line: 'Apps Web2/3 & móviles, smart contracts, cloud',
          },
          {
            id: 'embedded',
            title: 'Embebido',
            line: 'Firmware, BSP, drivers',
          },
          {
            id: 'hardware',
            title: 'Electrónica',
            line: 'PCB, RF, esquemas, prototipos',
          },
          {
            id: 'reverseEngineering',
            title: 'Ingeniería inversa',
            line: 'Software, hardware, RF, análisis',
          },
          {
            id: 'aiAssisted',
            title: 'Asistido por IA',
            line: 'Uso riguroso, guiada por expertos, lista para producción',
          },
          {
            id: 'industrialization',
            title: 'Industrialización',
            line: 'Preseries, producción, calidad, escala',
          },
        ],
      },
    },
    mission: {
      title: 'Nuestra experiencia',
      description: 'Sparkr ofrece un acompañamiento completo y personalizado, con una amplia experiencia en áreas como IA, IoT, RF, robótica, smartphones y tablets Android o blockchain.',
      intro: 'Nuestro conocimiento nos permite cubrir todas las competencias técnicas involucradas en la creación de un producto o un servicio:',
      expertise: [
        'diseño mecánico',
        'diseño de PCB',
        'desarrollo embebido, middleware, aplicación y cloud',
        'desarrollo de apps Web3 y smart contracts',
        'desarrollo de POCs y MVPs',
        'ingeniería inversa de software, hardware y RF',
        'seguimiento de proyecto',
        'preparación, lanzamiento y seguimiento de producción',
      ],
      aiTitle: 'IA y desarrollo: avanzar más rápido, con la exigencia de una supervisión experta',
      aiColumnHelpsLabel: 'La IA ahorra tiempo',
      aiColumnLimitsLabel: 'La experiencia garantiza el resultado',
      aiHelps: [
        'La IA acelera la exploración, las iteraciones, la producción de código, las pruebas y la documentación cuando se utiliza dentro de un marco metódico y riguroso.',
        'La utilizamos para entregar POCs y MVPs más rápido, en particular en lo aplicativo y en la nube, con una supervisión experta que garantiza una arquitectura sólida, un nivel de seguridad impecable y una base mantenible.',
      ],
      aiLimits: [
        'Validación de producto, ensayos, EMC/RF, cualificación y certificación: las decisiones se basan en pruebas medibles, validadas en laboratorio y sobre el terreno.',
        'Industrialización, cadena de suministro y puesta en producción: retos operativos que exigen experiencia, método y control, más allá de una simple demostración de software.',
      ],
    },
    services: {
      title: 'Nuestros servicios',
      description: 'Intervenimos en todos los niveles de la cadena, desde el desarrollo hasta la industrialización:',
      trackSoftware: 'Software y cloud',
      trackHardware: 'Hardware y embebido',
      trackBoth: 'Software y hardware',
      items: [
        {
          image: '/images/Design.jpg',
          title: 'Definición técnica de productos (arquitectura de software, electrónica y mecánica)',
          alt: 'Definición técnica de productos',
          track: 'both',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/POC.jpg',
          title: 'Desarrollo y realización de POC (para probar un algoritmo, un componente o una funcionalidad específica)',
          alt: 'Desarrollo de POC',
          track: 'both',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/MVP.jpg',
          title: 'Desarrollo y realización de MVP (producto mínimo viable para validar mercado y usos)',
          alt: 'Desarrollo de MVP',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/Features.jpg',
          title: 'Desarrollo de funcionalidades clave',
          alt: 'Desarrollo de funcionalidades clave',
          track: 'software',
          offerPage: 'poc-mvp',
        },
        {
          image: '/images/ReverseEngineeringSoftware.jpg',
          title: 'Ingeniería inversa de software (desensamblado, extracción algorítmica)',
          alt: 'Ingeniería inversa de software',
          track: 'software',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineering.jpg',
          title: 'Ingeniería inversa de hardware (placas, esquemas, extracción de firmware, análisis del existente)',
          alt: 'Ingeniería inversa de hardware',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/ReverseEngineeringRF.jpg',
          title: 'Ingeniería inversa RF (protocolos radio, modulación, codificación)',
          alt: 'Ingeniería inversa RF',
          track: 'hardware',
          offerPage: 'reverse-engineering',
        },
        {
          image: '/images/Prototypes.jpg',
          title: 'Desarrollo y realización de prototipos',
          alt: 'Desarrollo de prototipos',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Preseries.jpg',
          title: 'Realización de preseries',
          alt: 'Realización de preseries',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Certifications.jpg',
          title: 'Certificaciones',
          alt: 'Certificaciones',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
        {
          image: '/images/Massprod.jpg',
          title: 'Industrialización a gran escala',
          alt: 'Industrialización a gran escala',
          track: 'hardware',
          offerPage: 'a-to-z',
        },
      ],
    },
    clients: {
      title: 'Nuestros clientes',
      description:
        'Acompañamos a actores industriales y tecnológicos en productos embebidos, conectados e innovadores. Estas son algunas organizaciones con las que hemos trabajado.',
      items: [
        { name: 'Arkéa' },
        { name: 'Archos' },
        { name: 'Compilons' },
        { name: 'Delta Drone' },
        { name: 'Lunii' },
        { name: 'Securely' },
      ],
    },
    offerCards: {
      title: 'Nuestro acompañamiento',
      description:
        'Descubra nuestras tres ofertas de acompañamiento, pensadas para responder perfectamente a sus necesidades.',
      learnMore: 'Más información',
    },
    offerPages: [
      {
        id: 'a-to-z',
        slug: 'idea-to-production',
        cardImage: '/images/Design.jpg',
        cardImageAlt: 'Definición técnica y diseño de producto',
        title: 'De la idea a la producción',
        subtitle: 'Diseño, prototipado, certificación e industrialización',
        cardClientNeed:
          'Cuando necesita decidir, validar e industrializar, de la viabilidad a la producción en serie',
        cardTeaser:
          'Arquitectura de producto, prototipos, preseries, certificaciones y seguimiento de proveedores: un único interlocutor, con entregables concretos en cada etapa, ya sea para necesidades puntuales o para un acompañamiento en todo el ciclo.',
        pageIntro:
          'Desde el estudio de viabilidad inicial hasta la producción en serie, Sparkr cubre todo el ciclo de producto con una exigencia de calidad constante en cada etapa, tanto si necesita un apoyo puntual como un acompañamiento de extremo a extremo.',
        phases: [
          {
            title: 'Estudio de viabilidad y arquitectura técnica',
            subtitle: 'Cuando se necesita decidir qué construir y cómo',
            description:
              'Analizamos los retos del producto, validamos la viabilidad, definimos la arquitectura y redactamos especificaciones técnicas accionables para iniciar el desarrollo con un marco sólido.',
          },
          {
            title: 'Prototipado y validación',
            subtitle: 'Cuando se necesita estabilizar, probar, refinar',
            description:
              'Iteraciones de hardware y software, bancos de prueba, ensayos con usuarios y preparación de requisitos para una subida de madurez serena.',
          },
          {
            title: 'Industrialización',
            subtitle: 'Cuando se necesita escalar, certificar, producir',
            description:
              'Preseries, industrialización, calificación y certificaciones, con acompañamiento completo hasta la puesta en producción, incluyendo el seguimiento de producción y de proveedores.',
          },
        ],
        ctaLabel: 'Hablemos de su proyecto',
        seo: {
          title: 'De la idea a la producción | Sparkr',
          description:
            'Sparkr le acompaña desde la definición técnica hasta la industrialización: viabilidad, prototipado, certificaciones y producción en serie.',
        },
      },
      {
        id: 'poc-mvp',
        slug: 'poc-mvp',
        cardImage: '/images/Features.jpg',
        cardImageAlt: 'Desarrollo de POC y MVP',
        title: 'POC y MVP ultrarrápidos',
        subtitle: 'Desarrollo de software acelerado con IA, bajo supervisión experta',
        cardClientNeed:
          'Cuando necesita construir rápido, reducir riesgo y demostrar valor',
        cardTeaser:
          'POC y MVP de software a buen ritmo: IA con rigor, supervisión experta, código mantenible y seguridad considerada desde el inicio.',
        pageIntro:
          'Diseñamos y entregamos POCs y MVPs, en particular de software, a ritmo acelerado, gracias a un uso disciplinado de la IA bajo supervisión experta. El resultado: código seguro y production-ready desde el primer día.',
        phases: [
          {
            title: 'Desarrollo de POC y MVP acelerado con IA',
            subtitle: 'Cuando se necesita construir rápido, reducir riesgo, demostrar valor',
            description:
              'Diseño y desarrollo ultrarrápidos, posibles gracias a un uso riguroso de la IA guiado por nuestra experiencia técnica. Este enfoque permite alcanzar un nivel production-ready desde el inicio, combinando calidad de código, mantenibilidad, seguridad by design y buenas prácticas.',
          },
        ],
        ctaLabel: 'Hablemos de su proyecto',
        seo: {
          title: 'POC y MVP ultrarrápidos | Sparkr',
          description:
            'Sparkr diseña y entrega POCs y MVPs de software a ritmo acelerado con IA, bajo supervisión experta. Código production-ready desde el primer día.',
        },
      },
      {
        id: 'reverse-engineering',
        slug: 'reverse-engineering',
        cardImage: '/images/ReverseEngineering.jpg',
        cardImageAlt: 'Ingeniería inversa de software, hardware y RF',
        title: 'Ingeniería inversa y recuperación técnica',
        subtitle: 'Análisis de sistemas de software, hardware y RF',
        cardClientNeed:
          'Cuando necesita entender lo desplegado, documentar o reingeniería',
        cardTeaser:
          'Placas, firmware, protocolos y RF: devolvemos una visión accionable para interoperabilidad, migración o mantenimiento, acotada a sus restricciones.',
        pageIntro:
          '¿Necesita comprender un sistema desplegado, documentar su funcionamiento o preparar una migración? Analizamos placas, firmware, protocolos y comportamiento radio para entregar documentación técnica accionable.',
        phases: [
          {
            title: 'Ingeniería inversa y recuperación técnica',
            subtitle: 'Cuando se necesita entender lo desplegado, documentar, reingeniería',
            description:
              'Análisis estructurado de sistemas software, hardware y RF ya en campo: placas, firmware, protocolos y comportamiento en radio. Entregamos documentación accionable (interfaces, riesgos, lagunas) para interoperabilidad, migración, mantenimiento o rediseño, acotada a sus restricciones y cumplimiento.',
          },
        ],
        ctaLabel: 'Hablemos de su proyecto',
        seo: {
          title: 'Ingeniería inversa de software, hardware y RF | Sparkr',
          description:
            'Sparkr analiza sus sistemas existentes (software, hardware, RF) para interoperabilidad, migración o rediseño. Documentación técnica accionable.',
        },
      },
    ],
    contact: {
      title: 'Contáctenos',
      description: '¿Desea un presupuesto o simplemente quiere discutir su proyecto con nosotros? No dude en contactarnos usando el formulario de abajo o por correo electrónico en',
      suffix: 'describiendo brevemente su proyecto para que podamos acordar una cita.',
      email: 'contact@sparkr.tech',
    },
    about: {
      title: 'Acerca de Sparkr',
      description: 'Sparkr es una empresa liderada por',
      suffix: ', ingeniero de formación, apasionado por las nuevas tecnologías y la innovación, experto en el desarrollo y la industrialización de productos tecnológicos.',
      founders: [
        {
          name: 'Jean-Christophe Rona',
          linkedin: 'https://www.linkedin.com/in/jean-christophe-rona/',
          image: '/images/jc.jpg',
          alt: 'Jean-Christophe Rona',
        },
      ],
    },
    footer: {
      company: 'Sparkr',
      description: 'Su socio para el desarrollo y la industrialización de productos tecnológicos.',
      quickLinks: 'Enlaces rápidos',
      services: 'Nuestro acompañamiento',
      contact: 'Contacto',
      legal: 'Legal',
      privacy: 'Privacidad',
      terms: 'Términos',
      followUs: 'Síganos',
      copyright: '© Sparkr 2026',
      allRightsReserved: 'Todos los derechos reservados',
    },
    seo: {
      homeTitle: 'Sparkr',
      homeDescription:
        'Sparkr le acompaña en el desarrollo de su producto, desde la definición técnica hasta la industrialización, incluidos POCs, MVPs, prototipos, desarrollo Web3/smart contracts e ingeniería inversa (software, hardware, RF).',
      privacyTitle: 'Política de privacidad | Sparkr',
      privacyDescription:
        'Política de privacidad del sitio Sparkr: datos, cookies y sus derechos.',
      termsTitle: 'Términos de uso | Sparkr',
      termsDescription: 'Términos de uso del sitio web Sparkr.',
    },
  },
};
